/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument*/
// @ts-nocheck
// TODO(ross) 2026.05.28 - clean up comments
import { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Typography, Button, Box } from '@mui/material';
import { ColorMode } from 'theme';
import * as fc from 'd3fc';
import * as d3 from 'd3';
import { timeFormat } from 'd3-time-format';
import * as trading_signals from 'trading-signals';
import { v4 as uuidv4 } from 'uuid';
import { timeDay, timeHour, timeMinute, timeSecond } from 'd3-time';
import sortBy from 'lodash/sortBy';
import sortedUniqBy from 'lodash/sortedUniqBy';
import range from 'lodash/range';
import sortedIndexBy from 'lodash/sortedIndexBy';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import {
  OPTION_ORIGIN_TO_PALETTE,
  OPTION_ACCUM_TO_PALETTE,
  HiroAlertCategoryFromAlertCategory,
  HiroLineType,
  genericSymbolineParamsEqual,
} from 'types';
import {
  hiroChartTimeRangeState,
  timezoneState,
  stocksState_SUBSCRIBE_TO_POLLING_RENDER,
  currentSymHiroOverviewState,
  workerState,
  hiroAlertsState,
} from 'states';
import { unsupressedSetInterval } from 'util/poll';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';
import { formatAsCompactNumber } from 'util/shared/common';
import {
  dayjs,
  ET,
  MILLISECONDS_PER_DAY,
  NYSE_OPENING_DST_UTC_HOUR,
  NYSE_OPENING_STD_UTC_HOUR,
  NYSE_CLOSING_DST_UTC_HOUR,
  NYSE_CLOSING_STD_UTC_HOUR,
  NYSE_OPENING_ET_MINUTES,
  NYSE_OPENING_ET_HOUR,
  NYSE_CLOSING_ET_HOUR,
} from 'util/shared/date';
import { EtDate, UserTzDate } from 'types/shared';
import { useLog } from 'hooks';
import { useHiroKeyLevels } from 'hooks/hiro/useHiroKeyLevels';
import { useHiroCtx } from './HiroProvider';
import { useStreamingSocketCore } from 'hooks/bloomberg/streaming/useStreamingSocket';

import { HISTORY_BEGIN, HiroChartDatafeed } from './HiroDatafeed';
import { filterHiroAlerts } from 'util/hiro';
import {
  ChartSeries,
  DeltaChartSeries,
  PriceChartSeries,
  KeyLevelChartSeries,
  AlertsChartSeries,
  IndicatorSingleLineChartSeries,
  BollingerBandsChartSeries,
  MACDChartSeries,
  candlesYExtentAccessors,
  indicatorYExtentAccessors,
} from './HiroChartSeries';

import {
  hiroIndicatorValid,
  indicatorFromName,
  IndicatorResultType,
} from './HiroChart/controls/AvailableIndicators';
import { skipNonMarketHours } from 'components/sg_d3fc/skip_non_market_hours';

// consider a point as worthy of being highlighted if it is within the following
// percentage of the X axis away from where the cursor is.
const PLOT_CURSOR_HIGHLIGHT_POINT_X_PROPORTION_OFFSET_MAX = 0.1;
const MINIMUM_TICK_STRIDE_PROPORTION = 0.025;

const HIDE_LABELS_PAST_VISIBLE_DAYS = 1;
const Y_AXIS_WIDTH = 48;

interface HiroD3ChartProps {
  chartId: string;
  sym: string;
  deltaSeriesList: ChartSeries[];
  priceSeriesList: ChartSeries[];
  mainIndicatorList: ChartSeries[];
  bottomIndicatorList: ChartSeries[];
  hiroKeyLevels: ChartSeries;
  alertSeries: ChartSeries;
  alerts: ChartSeries;
  rerenderCnt: number;
  datafeedRef: Ref<HiroChartDatafeed>;
  hiroLineType: HiroLineType;
  resetZoomRef: Ref;
  autoRangeDeltaAxis: boolean;
  setAutoRangeDeltaAxis: (v: boolean) => void;
  autoRangePriceAxis: boolean;
  setAutoRangePriceAxis: (v: boolean) => void;
  showPrice: boolean;
}

const HiroD3Chart = ({
  chartId,
  sym,
  deltaSeriesList,
  priceSeriesList,
  mainPaneIndicatorList,
  bottomIndicatorList,
  hiroKeyLevels,
  alertSeries,
  rerenderCnt,
  datafeedRef,
  hiroLineType,
  resetZoomRef,
  autoRangeDeltaAxis,
  setAutoRangeDeltaAxis,
  autoRangePriceAxis,
  setAutoRangePriceAxis,
  showPrice,
}: HiroD3ChartProps) => {
  const theme = useTheme();
  const timeZone = useRecoilValue(timezoneState);
  const wrapperId = `${chartId}-wrpr`;
  const {
    priceLineType,
    includeHiroETH: showExtendedHours,
    mode: canvasMode,
  } = useHiroCtx();
  const showExtendedHoursRef = useRef(showExtendedHours);
  showExtendedHoursRef.current = showExtendedHours;

  // Two independent Y-axis zoom-state sentinels. Each is non-null when the
  // user has manually stretched / panned that axis; null means "follow data".
  const deltaZoomTransformRef = useRef(null); // right axis (HIRO delta)
  const priceZoomTransformRef = useRef(null); // left axis (price)
  // Delta domain captured at the start of each plot-area gesture so the zoom
  // callback can compute the incremental change and mirror it onto the price axis.
  const prevDeltaDomainRef = useRef(null);

  const autoRangeDeltaAxisRef = useRef(autoRangeDeltaAxis);
  autoRangeDeltaAxisRef.current = autoRangeDeltaAxis;
  const autoRangePriceAxisRef = useRef(autoRangePriceAxis);
  autoRangePriceAxisRef.current = autoRangePriceAxis;

  const yExtent = fc
    .extentLinear()
    .accessors(candlesYExtentAccessors)
    .pad([0.1, 0.1]);

  const botIndicatorsYExtent = fc
    .extentLinear()
    .accessors(indicatorYExtentAccessors)
    .pad([0.05, 0.05]);

  const xExtent = fc
    .extentDate()
    .accessors([
      (series) =>
        series.candles?.reduce(
          (accum, c) => Math.max(accum ?? c.date, c.date),
          null,
        ),
      (series) =>
        series.candles?.reduce(
          (accum, c) => Math.min(accum ?? c.date, c.date),
          null,
        ),
    ])
    .pad([0.0, 0.07]);

  const visiblePriceSeriesList = showPrice ? priceSeriesList : [];
  const showPriceRef = useRef(showPrice);
  showPriceRef.current = showPrice;
  const deltaAndPriceSeries = [...deltaSeriesList, ...visiblePriceSeriesList];
  const deltaAndPriceSeriesRef = useRef([]);
  deltaAndPriceSeriesRef.current = deltaAndPriceSeries;
  const allSeriesData = [
    ...deltaSeriesList,
    ...visiblePriceSeriesList,
    hiroKeyLevels,
    alertSeries,
    ...mainPaneIndicatorList,
  ];

  const xDomain = xExtent(deltaAndPriceSeries);
  const xDomainRef = useRef(xDomain);
  xDomainRef.current = xDomain;
  //if (isNaN(xDomain[0])) {
  //  xDomain = [new Date(), new Date()];
  //}
  // TODO(ross) 2026.05.06 - scaleDiscontinuous

  const chartXScaleRef = useRef(null);
  const setXScaleRef = () => {
    if (showExtendedHours) {
      chartXScaleRef.current = d3.scaleTime();
    } else {
      const scaleTime = d3.scaleTime();
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const oldTicks = scaleTime.ticks;
      const newTicks = (numTicks: number) => {
        // update number of ticks to account for gaps that will be filtered out
        const [start, end] = chartXScaleRef.current?.domain() ?? [NaN, NaN];
        if (isNaN(start) || isNaN(end)) {
          return oldTicks(numTicks);
        }
        const distanceMs = skipNonMarketHours.distance(start, end);
        const factor = (+end - +start) / distanceMs;
        numTicks = Math.round(numTicks * Math.max(1, 1 + (factor - 1) * 1.5));

        const now = new Date();
        const filteredTicks = sortedUniqBy(
          // eslint-disable-next-line @typescript-eslint/unbound-method
          oldTicks(numTicks)
            .map((d) => skipNonMarketHours.clampUp(d))
            .filter((d) => d < now),
          (d) => +d,
        );
        let filterOutNextTick = false;
        return filteredTicks.filter((d, idx) => {
          if (filterOutNextTick) {
            filterOutNextTick = false;
            return false;
          }
          const thisUtcHour = d.getUTCHours();
          if (
            thisUtcHour == NYSE_CLOSING_DST_UTC_HOUR ||
            thisUtcHour == NYSE_CLOSING_STD_UTC_HOUR
          ) {
            const nextPossibleOpen = filteredTicks[idx + 1];
            const nextUtcHour = nextPossibleOpen?.getUTCHours();
            if (
              nextUtcHour == NYSE_OPENING_DST_UTC_HOUR ||
              nextUtcHour == NYSE_OPENING_STD_UTC_HOUR
            ) {
              if (
                d.getUTCMinutes() == 0 &&
                d.getUTCSeconds() == 0 &&
                nextPossibleOpen.getUTCMinutes() == NYSE_OPENING_ET_MINUTES &&
                nextPossibleOpen.getUTCSeconds() == 0
              ) {
                return false;
              }
            }
          }

          const nextTick = filteredTicks[idx + 1];
          if (
            nextTick != null &&
            skipNonMarketHours.distance(d, nextTick) / distanceMs <
              MINIMUM_TICK_STRIDE_PROPORTION
          ) {
            if (skipNonMarketHours.isMarketOpen(d)) {
              filterOutNextTick = true;
            } else {
              return false;
            }
          }

          return true;
        });
      };

      scaleTime.ticks = newTicks;

      // eslint-disable-next-line @typescript-eslint/unbound-method
      const oldCopy = scaleTime.copy;
      scaleTime.copy = () => {
        const result = oldCopy();
        result.ticks = newTicks;
        return result;
      };

      const scaleDiscontinuous = fc
        .scaleDiscontinuous(scaleTime)
        .discontinuityProvider(skipNonMarketHours);

      chartXScaleRef.current = scaleDiscontinuous;

      chartXScaleRef.current.domain(xDomain).range([0, 640]);
    }
  };
  if (chartXScaleRef.current == null) {
    setXScaleRef();
  }
  // only sensitive to `showExtendedHours`.  The rest of the system continues to
  // update the other enclosed variables as needed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setXScaleRef, [showExtendedHours]);

  const chartYScaleRef = useRef(d3.scaleLinear());

  const botIndicatorsYScaleRef = useRef([]);
  botIndicatorsYScaleRef.current.length = Math.min(
    bottomIndicatorList.length,
    botIndicatorsYScaleRef.current.length,
  );
  range(bottomIndicatorList.length - botIndicatorsYScaleRef.current.length).map(
    (_) => botIndicatorsYScaleRef.current.push(d3.scaleLinear()),
  );

  const priceScaleRef = useRef(d3.scaleLinear());
  const priceExtentRef = useRef();
  priceExtentRef.current = yExtent(priceSeriesList);
  // Price-side domain: preserved if user has stretched and A is off; else fit data.
  if (
    priceZoomTransformRef.current != null &&
    !autoRangePriceAxis &&
    !isNaN(priceScaleRef.current.domain()[0])
  ) {
    // keep priceScaleRef.current.domain() as-is
  } else {
    priceScaleRef.current.domain(priceExtentRef.current);
  }

  const unzoomedDeltaDomainRef = useRef();
  unzoomedDeltaDomainRef.current = yExtent(
    deltaSeriesList.length ? deltaSeriesList : priceSeriesList,
  );
  useEffect(() => {
    if (autoRangeDeltaAxis) {
      // Re-enabling delta A wipes the delta-side stretch AND the X-axis
      // stretch (X-axis interactions also use deltaZoomTransformRef as their
      // "user has moved the timeline" sentinel). Leaves the price side alone.
      //deltaZoomTransformRef.current = null;
      chartYScaleRef.current.domain(
        unzoomedDeltaDomainRef.current ?? [NaN, NaN],
      );
      //chartXScaleRef.current.domain(xDomainRef.current ?? [NaN, NaN]);
    }
  }, [autoRangeDeltaAxis]);
  useEffect(() => {
    if (autoRangePriceAxis) {
      priceZoomTransformRef.current = null;
      priceScaleRef.current.domain(priceExtentRef.current ?? [NaN, NaN]);
    }
  }, [autoRangePriceAxis]);
  let yDomain;
  if (
    deltaZoomTransformRef.current != null &&
    !autoRangeDeltaAxis &&
    !isNaN(chartYScaleRef.current.domain()[0])
  ) {
    yDomain = chartYScaleRef.current.domain();
  } else {
    yDomain = unzoomedDeltaDomainRef.current;
    chartYScaleRef.current.domain(yDomain);
  }

  const botIndicatorsYDomains = bottomIndicatorList.map((bottomIndicator) =>
    botIndicatorsYExtent([bottomIndicator]),
  );

  const chartXDomain =
    isNaN(chartXScaleRef.current.domain()[0]) ||
    +chartXScaleRef.current.domain()[0] < HISTORY_BEGIN.valueOf()
      ? xDomain
      : chartXScaleRef.current.domain();

  const candleWidth = isNaN(chartXDomain[0])
    ? 2
    : (0.5 *
        (chartXScaleRef.current.range()[1] -
          chartXScaleRef.current.range()[0]) *
        datafeedRef.current.candleDurationMs) /
      (showExtendedHours
        ? chartXDomain[1].valueOf() - chartXDomain[0].valueOf()
        : skipNonMarketHours.distance(chartXDomain[0], chartXDomain[1]));

  const deltaScale = d3.scaleLinear().domain(yDomain);
  const deltaAxis = d3
    .axisRight(chartYScaleRef.current)
    .ticks(10)
    .tickFormat((d) => formatAsCompactNumber(d));

  // priceInDeltaScale projects a price into delta-space so it renders at the
  // SAME pixel as its price-axis label would. Use the CURRENT scale domains
  // (not the data extents) so a user-driven price-axis stretch is honored —
  // the price line tracks the stretched price view, not the original data.
  const priceInDeltaScale = (p) => {
    const deltaDomain = chartYScaleRef.current.domain();
    const priceDomain = priceScaleRef.current.domain();
    if (
      isNaN(deltaDomain[0]) ||
      isNaN(priceDomain[0]) ||
      priceDomain[1] === priceDomain[0]
    ) {
      return p;
    }
    const factor =
      (deltaDomain[1] - deltaDomain[0]) / (priceDomain[1] - priceDomain[0]);
    return (p - priceDomain[0]) * factor + deltaDomain[0];
  };

  // deltaInPriceScale is used to sync priceScale.domain off chartYScale.domain
  // on plot-area zoom. It must use the original DATA extents — current-scale
  // math would be self-referential (priceScale.domain is what we're computing).
  const deltaInPriceScale = (d) => {
    const deltaDomain = unzoomedDeltaDomainRef.current;
    const priceDomain = priceExtentRef.current;
    const factor = isNaN(deltaDomain[0])
      ? 1
      : (priceDomain[1] - priceDomain[0]) / (deltaDomain[1] - deltaDomain[0]);

    return (d - deltaDomain[0]) * factor + (priceDomain[0] ?? 0);
  };

  //const priceInDeltaScale = (p) => {
  //  const result =
  //    (p - priceScaleRef.current.domain()[0]) *
  //      deltaExtantFromPriceExtentFactor +
  //    (chartYScaleRef.current.domain()[0] ?? 0);
  //  if (zoomTransformRef.current != null) {
  //    return zoomTransformRef.current.applyY(result);
  //  } else {
  //    return result;
  //  }
  //};

  const multi = fc
    .seriesSvgMulti()
    .series([
      fc.annotationSvgGridline(),
      ...allSeriesData.flatMap((chartSeries) =>
        chartSeries.d3fcSeries(
          xDomain,
          priceScaleRef,
          chartXScaleRef,
          chartYScaleRef,
          priceInDeltaScale,
          candleWidth,
          theme.colorMode,
          hiroLineType,
        ),
      ),
    ])
    .mapping((data, index, _series) => {
      let adjustedIdx = index - 1; // -1 to skip gridlines series
      if (adjustedIdx < 0) {
        return;
      }
      for (const chartSeries of data) {
        const numD3fcSeries = chartSeries.numD3fcSeries();

        if (adjustedIdx < numD3fcSeries) {
          return chartSeries.d3Data();
        }
        adjustedIdx -= numD3fcSeries;
      }
    });

  const botIndicatorsMulti = bottomIndicatorList.map((bottomIndicator, i) =>
    fc
      .seriesSvgMulti()
      .series([
        ...bottomIndicator.d3fcSeries(
          xDomain,
          priceScaleRef,
          chartXScaleRef,
          botIndicatorsYScaleRef.current[i],
          priceInDeltaScale,
          candleWidth,
          theme.colorMode,
          hiroLineType,
        ),
        fc.annotationSvgGridline(),
      ])
      .mapping((data, index, _series) => {
        let adjustedIdx = index;
        for (const chartSeries of data) {
          const numD3fcSeries = chartSeries.numD3fcSeries();

          if (adjustedIdx < numD3fcSeries) {
            return chartSeries.d3Data();
          }
          adjustedIdx -= numD3fcSeries;
        }
      }),
  );

  const visibleDaysRef = useRef(null);
  if (!isNaN(xDomain[0])) {
    visibleDaysRef.current = showExtendedHours
      ? (xDomain[1].valueOf() - xDomain[0].valueOf()) / MILLISECONDS_PER_DAY
      : skipNonMarketHours.distance(xDomain[0], xDomain[1]) /
        MILLISECONDS_PER_DAY;
  }

  const timeFormatter = genTimeTickFormat(
    timeZone,
    visibleDaysRef.current,
    showExtendedHours,
  );

  const mainCrosshair = makeCrosshair({
    chartXScaleRef,
    chartYScaleRef,
    timeZone,
    displayTime: true,
  });
  const botCrosshair = makeCrosshair({
    chartXScaleRef,
    chartYScaleRef,
    timeZone,
    displayTime: false,
  });
  const crosshair = (selection) => {
    selection
      .filter(function () {
        return d3.select(this).attr('data-pane-type') == 'main';
      })
      .call(mainCrosshair);
    selection
      .filter(function () {
        return d3.select(this).attr('data-pane-type') != 'main';
      })
      .call(botCrosshair);
  };

  const onMouseMove = (plotSvg, event) => {
    const { clientX, clientY } = event;
    const {
      x: plotXOffset,
      y: plotYOffset,
      width: plotWidth,
    } = plotSvg.node().getBoundingClientRect();
    const cursorX = clientX - plotXOffset;
    const cursorY = clientY - plotYOffset;
    plotSvg.datum([{ x: cursorX, y: cursorY }]).call(crosshair);

    // find the closest points and line to highlight and display
    const cursorDate = chartXScaleRef.current.invert(cursorX);

    let candidateSeriesList = deltaAndPriceSeries.map((series) => {
      const gteIdx = sortedIndexBy(
        series.candles,
        { date: cursorDate },
        (c) => c.date,
      );

      let closestCandle = null;

      for (const candleIdx of [gteIdx - 1, gteIdx]) {
        const c = series.candles[candleIdx];
        if (c == null) {
          continue;
        }
        if (
          closestCandle == null ||
          Math.abs(cursorDate.valueOf() - c.date.valueOf()) <
            Math.abs(cursorDate.valueOf() - closestCandle.date.valueOf())
        ) {
          closestCandle = c;
        }
      }

      let xDiffToCursor = null;
      let yDiffToCursor = null;

      if (closestCandle != null) {
        xDiffToCursor = Math.abs(
          cursorX - chartXScaleRef.current(closestCandle.date),
        );

        for (const val of [closestCandle.high, closestCandle.low]) {
          const candleY = chartYScaleRef.current(
            series instanceof PriceChartSeries ? priceInDeltaScale(val) : val,
          );
          const thisYDiffToCursor = Math.abs(cursorY - candleY);
          if (yDiffToCursor == null || thisYDiffToCursor < yDiffToCursor) {
            yDiffToCursor = thisYDiffToCursor;
          }
        }
      }
      return {
        series,
        closestCandle,
        xDiffToCursor,
        yDiffToCursor,
      };
    });

    candidateSeriesList = sortBy(candidateSeriesList, (s) => s.yDiffToCursor);
    const candidateSeries = candidateSeriesList[0];
    if (
      candidateSeries?.xDiffToCursor != null &&
      candidateSeries?.yDiffToCursor != null &&
      candidateSeries?.xDiffToCursor <=
        plotWidth * PLOT_CURSOR_HIGHLIGHT_POINT_X_PROPORTION_OFFSET_MAX
    ) {
      const t = candidateSeries.closestCandle.time;

      const indicatorLabels = (prefixGenerator) => (ind) => {
        const indAry = ind.d3Data();
        const idx = sortedIndexBy(indAry, { time: t }, (c) => c.time);
        if (idx < 0 || idx >= indAry.length) {
          return;
        }
        const indDatum = indAry[idx];
        if (indDatum.time !== t) {
          return;
        }
        const prefix = prefixGenerator?.(ind) ?? '';

        return indicatorDisplayStrings(
          ind.indicatorMeta.resultType,
          ind.yScale,
          indDatum,
        ).map((valStr, idx) => ({
          color: ind.colors?.[idx] ?? ind.colors?.[0],
          text: `${prefix}${valStr}`,
        }));
      };

      candidateSeriesList = sortBy(candidateSeriesList, [
        (cs) => cs.series.name,
      ]).filter((cs) => cs.closestCandle.time == t);
      const mainChartSvg = d3.select(`#${chartId} svg`);
      const closestCandleDisplay = mainChartSvg
        .selectAll('g.current-candle-display')
        .data([
          ...candidateSeriesList.map((cs) => {
            const fmt =
              cs.series instanceof PriceChartSeries
                ? currencyLabel
                : deltaLabel;
            let candleStr;
            if (
              (cs.series.symbolLineParams.kind == 'price' &&
                priceLineType !== 'Line') ||
              (cs.series.symbolLineParams.kind == 'hiro' &&
                hiroLineType !== 'Line')
            ) {
              const { open, high, low, close, volume } = cs.closestCandle;
              candleStr = `O: ${fmt(open)} H: ${fmt(high)} L: ${fmt(low)} C: ${fmt(close)}`;
              if (
                cs.series.symbolLineParams.kind == 'price' &&
                (volume ?? 0) != 0
              ) {
                const volStr = formatAsCompactNumber(volume, {
                  maximumSignificantDigits: 3,
                });
                candleStr += ` V: ${volStr}`;
              }
            } else {
              candleStr = fmt(cs.closestCandle.close);
            }
            return {
              color: cs.series.colors[0],
              text: `${cs.series.name}: ${candleStr}`,
            };
          }),
          ...mainPaneIndicatorList
            .flatMap(indicatorLabels((ind) => `${ind.name}: `))
            .filter(Boolean),
        ]);

      const closestCandleDisplayG = closestCandleDisplay
        .enter()
        .append('g')
        .attr('class', 'current-candle-display')
        .attr('font-size', '12')
        .attr('transform', (_, i) => `translate(9, ${28 + 12 * i})`);
      closestCandleDisplayG.exit().remove();

      closestCandleDisplayG.append('text');

      closestCandleDisplay
        .merge(closestCandleDisplayG)
        .attr('fill', ({ color }) => color)
        .select('text')
        .text(({ text }) => text);

      for (const [idx, bottomIndicator] of bottomIndicatorList.entries()) {
        const bottomChart = d3.select(
          `#${wrapperId} #${bottomIndicator.id(idx)} .plot-area svg`,
        );
        const closestCandleDisplay = bottomChart
          .selectAll('g.current-candle-display')
          .data(indicatorLabels()(bottomIndicator)?.filter(Boolean) ?? []);

        const closestCandleDisplayG = closestCandleDisplay
          .enter()
          .append('g')
          .attr('class', 'current-candle-display')
          .attr('font-size', '12')
          .attr('transform', (_, i) => `translate(9, ${14 + 12 * i})`);
        closestCandleDisplayG.exit().remove();

        closestCandleDisplayG.append('text');

        closestCandleDisplay
          .merge(closestCandleDisplayG)
          .attr('fill', ({ color }) => color)
          .select('text')
          .text(({ text }) => text);
      }
    }

    for (const chartSvgSelector of [
      `#${chartId} svg`,
      ...bottomIndicatorList.map(
        (i, idx) => `#${wrapperId} #${i.id(idx)} .plot-area svg`,
      ),
    ]) {
      const chart = d3.select(chartSvgSelector);
      if (chart.node() == plotSvg.node()) {
        continue;
      }

      chart.datum([{ x: cursorX, y: -1e6 }]).call(crosshair);
    }
  };
  const onMouseMoveRef = useRef(onMouseMove);
  onMouseMoveRef.current = onMouseMove;

  const renderRef = useRef<() => void, null>(null);

  const timeDomainInFlux = (): boolean => {
    const curDomain = chartXScaleRef.current.domain();
    return isNaN(curDomain[0]) || isNaN(curDomain[1]);
  };

  const zoomRef = useRef();
  if (zoomRef.current == null) {
    zoomRef.current = fc.zoom().on('zoom', (e) => {
      if (timeDomainInFlux()) {
        return;
      }

      // fc.zoom directly mutates chartYScale and chartXScale via its transform.
      // Record on the delta side; the X-axis-stretch path also uses this ref
      // as its "user has manually moved the timeline" sentinel.
      const prevDeltaDomain = prevDeltaDomainRef.current;
      deltaZoomTransformRef.current = e.transform;

      renderRef.current?.();

      if (autoRangePriceAxisRef.current) {
        // Price autoscale: convert the new delta domain into price space.
        priceScaleRef.current.domain(
          chartYScaleRef.current.domain().map(deltaInPriceScale),
        );
      } else if (prevDeltaDomain != null) {
        // Price manually positioned: apply the same proportional zoom/pan.
        // Derive scale factor k and anchor pixel from the incremental delta
        // domain change, then replay on the price domain.
        const newD = chartYScaleRef.current.domain();
        const oldSpan = prevDeltaDomain[1] - prevDeltaDomain[0];
        const newSpan = newD[1] - newD[0];
        if (isFinite(oldSpan) && oldSpan !== 0) {
          const k = newSpan / oldSpan;
          const prevP = priceScaleRef.current.domain();
          const pSpan = prevP[1] - prevP[0];
          if (isFinite(pSpan)) {
            const range = chartYScaleRef.current.range();
            const H = Math.abs(range[0] - range[1]) || 1;
            let newPriceDomain;
            if (Math.abs(k - 1) < 1e-10) {
              // Pure pan: same fractional shift applied to price span.
              const priceShift =
                ((newD[0] - prevDeltaDomain[0]) / oldSpan) * pSpan;
              newPriceDomain = [prevP[0] + priceShift, prevP[1] + priceShift];
            } else {
              // Zoom (possibly with pan): find the invariant pixel then
              // zoom the price domain around the same pixel position.
              const anchorDelta = (newD[0] - k * prevDeltaDomain[0]) / (1 - k);
              // range is [H, 0]: pixel 0 → domain[1], pixel H → domain[0]
              const py =
                (H * (prevDeltaDomain[1] - anchorDelta)) /
                (prevDeltaDomain[1] - prevDeltaDomain[0]);
              const pAnchor = prevP[1] - pSpan * (py / H);
              newPriceDomain = [
                pAnchor - (pAnchor - prevP[0]) * k,
                pAnchor + (prevP[1] - pAnchor) * k,
              ];
            }
            priceScaleRef.current.domain(newPriceDomain);
          }
        }
      }

      prevDeltaDomainRef.current = chartYScaleRef.current.domain().slice();

      const timeRange = chartXScaleRef.current.domain();
      if (!isNaN(timeRange[0])) {
        datafeedRef.current.debouncedOnVisibleRangeChanged({
          from: dayjs(timeRange[0]),
          to: dayjs(timeRange[1]),
        });
      }
    });
  }

  // Drag on the RIGHT (delta) axis stretches the delta scale only.
  // First drag flips delta-A off.
  const yDeltaDragRef = useRef(
    makeYAxisStretchDrag({
      autoRangeRef: autoRangeDeltaAxisRef,
      disableAutoRange: () => setAutoRangeDeltaAxis(false),
      scaleRef: chartYScaleRef,
      zoomTransformRef: deltaZoomTransformRef,
      stateKey: '__yDeltaDragStart',
      renderRef,
      // Keep prevDeltaDomainRef in sync so the next plot-area zoom event
      // computes the correct incremental change rather than jumping.
      afterDrag: () => {
        prevDeltaDomainRef.current = chartYScaleRef.current.domain().slice();
      },
    }),
  );

  // Drag on the LEFT (price) axis stretches the price scale only.
  // First drag flips price-A off.
  const yPriceDragRef = useRef(
    makeYAxisStretchDrag({
      autoRangeRef: autoRangePriceAxisRef,
      disableAutoRange: () => setAutoRangePriceAxis(false),
      scaleRef: priceScaleRef,
      zoomTransformRef: priceZoomTransformRef,
      stateKey: '__yPriceDragStart',
      renderRef,
    }),
  );

  // Drag on time axis compresses/expands timeline anchored at right edge ("now").
  const xAxisDragRef = useRef(
    d3
      .drag()
      .on('start', function (event) {
        if (timeDomainInFlux()) {
          return;
        }
        const xScale = chartXScaleRef.current;
        const range = xScale.range();
        const width = Math.abs(range[1] - range[0]) || 1;
        const anchorPx = Math.max(range[0], range[1]);
        const anchorDate = xScale.invert(anchorPx);
        const anchorTime = +anchorDate;
        d3.select(this).property('__xDragStart', {
          domain: xScale.domain().map((d) => +d),
          domainDates: xScale.domain(),
          anchorTime,
          anchorDate,
          startX: event.x,
          width,
        });
      })
      .on('drag', function (event) {
        if (timeDomainInFlux()) {
          return;
        }
        const s = d3.select(this).property('__xDragStart');
        if (s == null) {
          return;
        }
        const factor = Math.exp(-(s.startX - event.x) / s.width);
        const newDomain = showExtendedHoursRef.current
          ? [
              new Date(s.anchorTime - (s.anchorTime - s.domain[0]) * factor),
              new Date(s.anchorTime + (s.domain[1] - s.anchorTime) * factor),
            ]
          : [
              skipNonMarketHours.offset(
                s.anchorDate,
                -skipNonMarketHours.distance(s.domainDates[0], s.anchorDate) *
                  factor,
              ),
              skipNonMarketHours.offset(
                s.anchorDate,
                skipNonMarketHours.distance(s.anchorDate, s.domainDates[1]) *
                  factor,
              ),
            ];

        newDomain.sort((d1, d2) => d1 - d2);
        chartXScaleRef.current.domain(newDomain);
        renderRef.current?.();
        datafeedRef.current.debouncedOnVisibleRangeChanged({
          from: dayjs(newDomain[0]),
          to: dayjs(newDomain[1]),
        });
      }),
  );

  const chart = fc
    .chartCartesian(chartXScaleRef.current, chartYScaleRef.current)
    .yTickFormat((_d) => '') // Blank and drawn in `draw.y-axis` event so the z-ordering is correct w.r.t. curent price label
    .xTickFormat((d) => d != null && timeFormatter(d))
    .svgPlotArea(multi);
  if (botIndicatorsMulti.length > 0) {
    // suppress tick creation on main pane and place on bottom indicator
    chart.xTicks(0);
  }

  const botIndicatorsCharts = botIndicatorsMulti.map((multi, i) => {
    const botChart = fc
      .chartCartesian(chartXScaleRef.current, botIndicatorsYScaleRef.current[i])
      .xTickFormat((d) => d != null && timeFormatter(d))
      .yTicks(3)
      .yTickFormat((d) => d != null && formatAsCompactNumber(d))
      .svgPlotArea(multi)
      .xDomain(chartXDomain)
      .yDomain(botIndicatorsYDomains[i])
      .decorate((selection) => {
        selection
          .enter()
          // additionally add a d3fc-svg element for the axis
          .append('d3fc-svg')
          // move the element into the right-axis cell
          .style('grid-column', 1)
          .style('grid-row', 3)
          // and set the axis width
          .style('width', `${Y_AXIS_WIDTH}px`);

        selection.on('measure.y-axis', (event) => {
          botIndicatorsYScaleRef.current[i].range([
            d3.select(event.currentTarget).select('d3fc-svg.y-axis').node()
              .offsetHeight,
            0,
          ]);
        });
        selection
          .enter()
          .call(setZoomNodeState, true, false)
          .selectAll('.x-axis')
          .on('.zoom', null)
          .call(xAxisDragRef.current)
          .style('cursor', 'ew-resize');

        const plotSvg = selection.select('svg');
        plotSvg
          .on('draw', () => {
            plotSvg.datum(mouseLocation).call(crosshair);
          })
          .on('mousemove', (event) => onMouseMoveRef.current(plotSvg, event))
          .on('mouseout', (_event) => {
            const mainChart = d3.select(`#${chartId} svg`);
            mainChart.selectAll('g.current-candle-display').remove();
            mainChart.datum([]).call(crosshair);
            for (const [
              idx,
              bottomIndicator,
            ] of bottomIndicatorList.entries()) {
              const plotContainer = d3.select(
                `#${wrapperId} #${bottomIndicator.id(idx)} .plot-area svg`,
              );
              plotContainer.datum([]).call(crosshair);
              plotContainer.selectAll('g.current-candle-display').remove();
            }
          });
      });

    if (i < botIndicatorsMulti.length - 1) {
      // suppress tick creation to only the bottom indicator
      botChart.xTicks(0);
    }

    return botChart;
  });

  chart.xDomain(chartXDomain);
  //chart.yDomain(yDomain);
  if (deltaZoomTransformRef.current == null || autoRangeDeltaAxis) {
    chart.yDomain(yDomain); // isNaN(chartYScaleRef.current.domain()[0]) ? yDomain : chartYScaleRef.current.domain());
  }

  const setZoomNodeState = (
    selection,
    includeX: boolean,
    includeY: boolean,
  ) => {
    const xScale = includeX ? chartXScaleRef.current : null;
    // Plot-area Y pan/zoom still routes through the delta scale (fc.zoom owns
    // chartYScale). Gated on the delta-A flag — when delta-A is on, plot-area
    // drag is X-only so autoscale stays undisturbed. The price scale follows
    // via the zoom callback (also gated on price-A).
    const yScale =
      includeY && !autoRangeDeltaAxisRef.current
        ? chartYScaleRef.current
        : null;
    if (xScale == null && yScale == null) {
      selection.on('.zoom', null);
    } else {
      zoomRef.current(selection, xScale, yScale);
    }
  };

  const setAllZoomNodeStates = (selection) => {
    // Plot area: wheel/drag pan+zoom via fc.zoom (Y gated by delta-A).
    selection.selectAll('.plot-area').call(setZoomNodeState, true, true);

    // Time axis: detach fc.zoom, attach drag-to-stretch.
    selection
      .selectAll('.x-axis')
      .on('.zoom', null)
      .call(xAxisDragRef.current)
      .style('cursor', 'ew-resize');

    // Left (price) axis: drag stretches the price scale only.
    selection
      .selectAll('.price-axis')
      .on('.zoom', null)
      .call(yPriceDragRef.current)
      .style('cursor', 'ns-resize');

    // Right (delta) axis + the right-edge listener overlay: drag stretches
    // the delta scale only.
    selection
      .selectAll('.y-axis, .delta-axis-zoom-listener-overlay')
      .on('.zoom', null)
      .call(yDeltaDragRef.current)
      .style('cursor', 'ns-resize');
  };
  useEffect(() => {
    d3.select(`#${chartId}`).call(setAllZoomNodeStates);
    // Rebind whenever either A flag flips, so the plot-area Y gating stays
    // current and any newly-mounted axis nodes pick up their drag handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRangeDeltaAxis, autoRangePriceAxis]);

  chart.decorate((selection) => {
    const plotContainer = selection.enter().select('d3fc-svg.plot-area');
    const plotSvg = plotContainer.select('svg');
    plotSvg.attr('data-pane-type', 'main');
    plotSvg
      .on('draw', () => {
        plotSvg.datum(mouseLocation).call(crosshair);
      })
      .on('mousemove', (event) =>
        onMouseMoveRef.current(plotSvg, event, crosshair),
      )
      .on('mouseout', (_event) => {
        plotSvg.datum([]).call(crosshair);
        plotSvg.select('.annotation-crosshair').remove();
        plotSvg.selectAll('g.current-candle-display').remove();
        for (const [idx, bottomIndicator] of bottomIndicatorList.entries()) {
          const plotContainer = d3.select(
            `#${wrapperId} #${bottomIndicator.id(idx)} .plot-area svg`,
          );
          plotContainer.datum([]).call(crosshair);
          plotContainer.select('.annotation-crosshair').remove();
          plotContainer.selectAll('g.current-candle-display').remove();
        }

        //plotContainer.requestRedraw();
      });

    // when the chart is added to the DOM
    selection
      .selectAll('d3fc-svg.price-axis')
      .data([true])
      .enter()
      // additionally add a d3fc-svg element for the axis
      .append('d3fc-svg')
      .attr('class', 'price-axis')
      .attr('id', 'hiroPriceScale')
      // move the element into the right-axis cell
      .style('grid-column', 1)
      .style('grid-row', 3)
      // and set the axis width
      .style('width', `${Y_AXIS_WIDTH}px`)
      // when there's a measure event (namespaced to avoid removing existing handlers)
      .on('measure.z-axis', (event) => {
        // set the range on the scale to the elements height
        priceScaleRef.current.range([event.detail.height, 0]);
      })
      .on('draw.z-axis', (event, _deltaAndPriceSeries) => {
        const axisG = d3
          .select(event.currentTarget)
          .style('visibility', showPriceRef.current ? 'visible' : 'hidden')
          .select('svg')
          .selectAll('g.left-axis')
          .data([{}]);

        const axisGenter = axisG
          .enter()
          .append('g')
          .attr('class', 'left-axis')
          .attr('transform', `translate(${Y_AXIS_WIDTH}, 0)`);

        const priceAxis = fc.axisLeft(priceScaleRef.current).ticks(8);

        axisG.merge(axisGenter).call(priceAxis);

        drawYAxisClosingVals(
          d3.select(event.currentTarget).select('d3fc-svg#hiroPriceScale svg'),
          deltaAndPriceSeriesRef.current.filter(
            (s) => s instanceof PriceChartSeries,
          ),
          priceAxis.scale(),
          (n) =>
            Intl.NumberFormat('en', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(n),
        );
      });

    selection
      .selectAll('div.delta-axis-zoom-listener-overlay')
      .data([true])
      .enter()
      .append('div')
      .attr('class', 'delta-axis-zoom-listener-overlay')
      .style('grid-column', 4)
      .style('grid-row', 3)
      .style('width', `${Y_AXIS_WIDTH}px`)
      .style('height', '100%')
      .style('z-index', '999');

    selection
      .enter()
      .on('draw.y-axis', (event, _deltaAndPriceSeries) => {
        const deltaSeriesList = deltaAndPriceSeriesRef.current.filter(
          (s) => s instanceof DeltaChartSeries,
        );
        const yAxisSvg = d3
          .select(event.currentTarget)
          .select('d3fc-svg.y-axis.right-axis svg')
          .style('opacity', deltaSeriesList.length == 0 ? '0' : '1');
        yAxisSvg
          .append('g')
          .attr('class', 'delta-axis-override')
          .call(deltaAxis);

        drawYAxisClosingVals(
          yAxisSvg,
          deltaSeriesList,
          chartYScaleRef.current,
          (n) => formatAsCompactNumber(n, { maximumSignificantDigits: 3 }),
        );
      })
      .on('measure.y-axis', (event) => {
        deltaScale.range([
          d3.select(event.currentTarget).select('d3fc-svg.y-axis').node()
            .offsetHeight,
          0,
        ]);
      });
    selection.enter().call(setAllZoomNodeStates);
  });

  resetZoomRef.current = (xOnly?: boolean) => {
    deltaZoomTransformRef.current = null;
    chartXScaleRef.current.domain([NaN, NaN]);
    const chartSel = d3.select(`#${chartId}`);
    if (!(xOnly ?? false)) {
      priceZoomTransformRef.current = null;
      chartYScaleRef.current.domain(
        unzoomedDeltaDomainRef.current ?? [NaN, NaN],
      );
      priceScaleRef.current.domain(priceExtentRef.current ?? [NaN, NaN]);
      chartSel
        .selectAll(
          '.plot-area, .x-axis, .price-axis, .y-axis, .delta-axis-zoom-listener-overlay',
        )
        .each(function () {
          this.__zoom = d3.zoomIdentity;
          delete this.__yDeltaDragStart;
          delete this.__yPriceDragStart;
          delete this.__xDragStart;
        });
    }
    chartSel.call(setAllZoomNodeStates);
  };

  const debounceResetRef = useRef(null);
  if (debounceResetRef.current == null) {
    debounceResetRef.current = debounce(() => {
      const curDomain = chartXScaleRef.current.domain();
      if (
        (curDomain[0] instanceof Date && isNaN(curDomain[0])) ||
        (curDomain[1] instanceof Date && isNaN(curDomain[1]))
      ) {
        resetZoomRef.current();
      }
    }, 2_000);
  }

  {
    const curDomain = chartXScaleRef.current.domain();
    if (
      (curDomain[0] instanceof Date && isNaN(curDomain[0])) ||
      (curDomain[1] instanceof Date && isNaN(curDomain[1]))
    ) {
      debounceResetRef.current();
    }
  }

  useEffect(() => {
    if (
      deltaZoomTransformRef.current != null ||
      priceZoomTransformRef.current != null
    ) {
      resetZoomRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sym]);

  const lastSymRendered = useRef<string | null>(null);

  const render = () => {
    if (isNaN(xDomain[0])) {
      return;
    }
    const chartDiv = d3.select(`#${chartId}`);
    if (lastSymRendered.current != null && lastSymRendered.current != sym) {
      lastSymRendered.current = sym;
      chartDiv.selectAll('.plot-area').each(function () {
        d3.select(this).call(zoomRef.current.transform, d3.zoomIdentity);
      });
      deltaZoomTransformRef.current = null;
      priceZoomTransformRef.current = null;
      chartXScaleRef.current.domain([NaN, NaN]);
    }
    //if (lastSymRendered.current == null) {
    //  lastSymRendered.current = sym;
    //}

    const validChartIds = allSeriesData.map((s) => s.id(hiroLineType));
    chartDiv.selectAll('.multi>g.chart-series-group').each(function (_, idx) {
      const sel = d3.select(this);
      if (sel.attr('id') != validChartIds[idx]) {
        sel.remove();
      }
    });

    chartDiv.datum(allSeriesData).call(chart);

    bottomIndicatorList.map((bottomIndicator, i) => {
      d3.select(`#${wrapperId} #${bottomIndicator.id(i)}`)
        .datum([bottomIndicator])
        .call(botIndicatorsCharts[i]);
    });

    d3.selectAll('g.hiro-closing-y-axis-label').raise();
  };

  renderRef.current = render;

  useEffect(() => {
    render();
    // skip ``render` dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deltaSeriesList,
    priceSeriesList,
    hiroKeyLevels,
    alertSeries,
    rerenderCnt,
    hiroLineType,
    sym,
    autoRangeDeltaAxis,
    autoRangePriceAxis,
  ]);

  const classes = ['d3fc-hiro-chart'];
  classes.push(
    `d3fc-chart-${theme.colorMode === ColorMode.LIGHT ? 'light' : 'dark'}`,
  );
  if (
    visibleDaysRef.current != null &&
    visibleDaysRef.current > HIDE_LABELS_PAST_VISIBLE_DAYS
  ) {
    classes.push('labels-on-hover');
  }

  const mainPaneClasses = [...classes];
  if (bottomIndicatorList.length > 0) {
    mainPaneClasses.push('hide-bottom-axis');
  }

  return (
    <Box
      id={wrapperId}
      sx={{
        width: '100%',
        minHeight: canvasMode == 'standalone' ? '45vh' : undefined,
        maxHeight: '95vh',
        height: '100%',
        display: 'grid',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flexGap: 0,
          gridArea: '1 / 1',
          paddingBottom: '12px',
        }}
      >
        <div
          style={{
            flexGrow: 3,
            width: '100%',
            display: 'grid',
          }}
        >
          <div
            id={chartId}
            style={{
              width: '100%',
              gridArea: '1 / 1',
            }}
            className={mainPaneClasses.join(' ')}
          />
          {/* Main Pane UI overlay */}
          <div
            style={{
              width: '100%',
              gridArea: '1 / 1',
            }}
          >
            <div
              style={{
                display: 'flex',
                height: '100%',
                width: '100%',
                flexDirection: 'column',
                alignItems: 'stretch',
              }}
            >
              <div></div>
              <div style={{ flexGrow: 1 }}></div>
              <div
                style={{
                  paddingBottom: '3px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'stretch',
                }}
              >
                <div style={{ paddingLeft: `${(1 / 3) * Y_AXIS_WIDTH}px` }}>
                  {/*Main Pane, bottom left corner — price-axis autoscale */}
                  {showPrice && (
                    <Button
                      variant={autoRangePriceAxis ? 'contained' : 'outlined'}
                      w={1}
                      sx={{
                        padding: 0,
                        fontSize: 11,
                        minWidth: '1.5em',
                        marginBottom:
                          bottomIndicatorList.length == 0 ? '2em' : undefined,
                      }}
                      onClick={() => setAutoRangePriceAxis(!autoRangePriceAxis)}
                      title={`Click to ${autoRangePriceAxis ? 'disable' : 'enable'} auto-range for the price (left) axis.`}
                    >
                      B
                    </Button>
                  )}
                </div>
                <div style={{ flexGrow: 1 }}></div>
                <div style={{ paddingRight: `${(2 / 3) * Y_AXIS_WIDTH}px` }}>
                  {/*Main Pane, bottom right corner — delta-axis autoscale */}
                  <Button
                    variant={autoRangeDeltaAxis ? 'contained' : 'outlined'}
                    w={1}
                    sx={{
                      padding: 0,
                      fontSize: 11,
                      minWidth: '1.5em',
                      marginBottom:
                        bottomIndicatorList.length == 0 ? '2em' : undefined,
                    }}
                    onClick={() => setAutoRangeDeltaAxis(!autoRangeDeltaAxis)}
                    title={`Click to ${autoRangeDeltaAxis ? 'disable' : 'enable'} auto-range for the HIRO delta (right) axis.`}
                  >
                    A
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {bottomIndicatorList.map((indicator, i) => (
          <div
            key={indicator.id(i)}
            style={{
              flexGrow: 1,
              width: '100%',
              marginTop: '-20px',
              paddingBottom: '4px',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                paddingLeft: `${Y_AXIS_WIDTH}px`,
                position: 'relative',
                top: '20px',
              }}
            >
              {indicator.name}
            </Typography>
            <div
              id={indicator.id(i)}
              style={{
                width: '100%',
                height: '100%',
              }}
              className={[
                ...classes,
                i < bottomIndicatorList.length - 1 ? 'hide-bottom-axis' : '',
              ].join(' ')}
            />
          </div>
        ))}
      </div>

      {/* full container UI overlay */}
      <div
        style={{
          width: '100%',
          gridArea: '1 / 1',
        }}
      >
        <div
          style={{
            display: 'flex',
            height: '100%',
            width: '100%',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <div></div>
          <div style={{ flexGrow: 1 }}></div>
          <div
            style={{
              paddingBottom: '3px',
              width: '100%',
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            <div>{/*full container bottom left corner */}</div>
            <div style={{ flexGrow: 1 }}></div>
            <div style={{ paddingRight: `${(2 / 3) * Y_AXIS_WIDTH}px` }}>
              {/*full container bottom right corner */}
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

export const HiroChartV3 = () => {
  const {
    sym,
    includeHiroETH: showExtendedHours,
    hiroRollingWindowSeconds,
    selectedLenses,
    optionType,
    candleDuration,
    hiroLineType,
    priceLineType,
    showAlerts,
    showPrice,
    showHiroKeyLevels: showKeyLevels,
    hiroIndicators,
    update,
    startQueryDate,
    endQueryDate,
    mode: containerMode,
    hiroSeriesColors,
  } = useHiroCtx();
  const { all: allTradesEnabled, nextExp: nextExpiryEnabled } = selectedLenses;
  const totalOptionAccumulationEnabled = optionType === 'total';
  const putOptionAccumulationEnabled = optionType === 'pc';
  const callOptionAccumulationEnabled = optionType === 'pc';
  const candleDurationMs = candleDuration * 1_000;

  const webWorker = useRecoilValue(workerState);
  const datafeed = useRef<undefined | HiroChartDatafeed>();
  const setHiroTimeRange = useSetRecoilState(hiroChartTimeRangeState);
  const theme = useTheme();
  const resetZoomRef = useRef();

  useEffect(
    () => datafeed.current?.onIntervalChanged(candleDurationMs),
    [candleDurationMs],
  );

  // Two independent A (autoscale) flags. Each governs one axis only.
  const [autoRangeDeltaAxis, setAutoRangeDeltaAxis] = useState(true);
  const [autoRangePriceAxis, setAutoRangePriceAxis] = useState(true);

  useEffect(() => {
    if (
      datafeed.current == null ||
      startQueryDate == null ||
      endQueryDate == null ||
      resetZoomRef.current == null ||
      startQueryDate.isAfter(endQueryDate, 'day')
    ) {
      return;
    }
    const now = dayjs().tz(ET);
    let to = null;
    if (!endQueryDate.isSameOrAfter(now, 'day')) {
      if (showExtendedHours) {
        to = endQueryDate.tz(ET).add(1, 'day').startOf('day');
      } else {
        to = endQueryDate
          .tz(ET)
          .hour(NYSE_CLOSING_ET_HOUR)
          .minute(0)
          .second(0)
          .millisecond(0);
      }
    }

    if (!startQueryDate.isSameOrBefore(now, 'day')) {
      return;
    }
    let from;
    if (showExtendedHours) {
      from = startQueryDate.tz(ET).startOf('day');
    } else {
      from = (
        startQueryDate.isSame(now, 'day')
          ? dayjs(skipNonMarketHours.clampDown(now.toDate()))
          : startQueryDate
      )
        .tz(ET)
        .hour(NYSE_OPENING_ET_HOUR)
        .minute(NYSE_OPENING_ET_MINUTES)
        .second(0)
        .millisecond(0);
    }
    setAutoRangeDeltaAxis(true);
    setAutoRangePriceAxis(true);
    resetZoomRef.current?.();
    datafeed.current.userSpecifiedCandleDurationMs = candleDuration * 1_000;
    datafeed.current.onVisibleRangeChanged({
      from,
      to,
    });
    datafeed.current.debouncedOnVisibleRangeChanged({
      from,
      to,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startQueryDate, endQueryDate]);

  const currentGlobalStocksState = useRecoilValue(
    stocksState_SUBSCRIBE_TO_POLLING_RENDER,
  );
  const setHiroAlerts = useSetRecoilState(hiroAlertsState);

  const { fetchAPIWithLog } = useLog('HiroChart');

  // eslint-disable-next-line prefer-const
  let [rerenderCnt, setRerenderCnt] = useState(0);

  useEffect(() => {
    const currentSession = showExtendedHours ? 'extended' : 'regular';
    if (
      datafeed.current != null &&
      datafeed.current.currentSession != currentSession
    ) {
      datafeed.current.currentSession = currentSession;
      datafeed.current.resetData();
      let from = datafeed.current.beginVisibleRange;
      if (currentSession == 'extended') {
        if (
          datafeed.current.endVisibleRange == null ||
          // test whether end is within 30 minutes of the latest RTH close
          // for whether to switch to current day
          skipNonMarketHours.clampDown(new Date()) -
            skipNonMarketHours.clampDown(
              datafeed.current.endVisibleRange.toDate(),
            ) <
            30 * 60 * 1_000
        ) {
          from = dayjs().tz(ET).startOf('day');
        } else {
          from = from.tz(ET).startOf('day');
        }
      } else {
        from = dayjs(skipNonMarketHours.clampDown(from.toDate()))
          .tz(ET)
          .hour(NYSE_OPENING_ET_HOUR)
          .minute(NYSE_OPENING_ET_MINUTES)
          .second(0)
          .millisecond(0);
      }
      let to = datafeed.current.endVisibleRange;

      if (from > dayjs()) {
        from = from.subtract(1, 'day');
        to = from.hour(NYSE_CLOSING_ET_HOUR).minute(0).second(0).millisecond(0);
      }
      if (to <= from) {
        to = null;
      }
      datafeed.current.onVisibleRangeChanged({
        from,
        to,
      });
      resetZoomRef.current?.();
    }
    // eslint-disable-next-line prefer-const
  }, [showExtendedHours]);

  if (datafeed.current == null) {
    datafeed.current = new HiroChartDatafeed(
      hiroRollingWindowSeconds / 60,
      setHiroAlerts,
      () => setRerenderCnt(++rerenderCnt),
      showExtendedHours ? 'extended' : 'regular',
      resetZoomRef,
    );

    datafeed.current.fetchAPIWithLog = fetchAPIWithLog;
    // throttle broadcasting too much as dependent code launches fetches immediately
    datafeed.current.broadCastTimeRangeUpdate = debounce(setHiroTimeRange, 200);
    datafeed.current.updateHiroCtx = update;
  }
  useEffect(
    () =>
      datafeed.current.setRollingWindowMinutes(hiroRollingWindowSeconds / 60),
    [hiroRollingWindowSeconds],
  );

  const streamSocket = useRef<WebSocket | null>(null);
  const { sendStreamControlMsg, reconnectSocket } = useStreamingSocketCore({
    enabled: true,
    socket: streamSocket,
    socketRetries: useRef(0),
    onOpen: () => datafeed.current.reRegisterCandleSubscriptions(),
    onReconnect: () => datafeed.current.reRegisterCandleSubscriptions(),
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    updateCandleCallback: (...args) =>
      datafeed.current.listenCandlesStream(...args),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reconnectSocket, []);

  const currentDatafeed = datafeed.current;

  datafeed.current.sendStreamControlMsg = sendStreamControlMsg;
  datafeed.current.isStreamOpen = () =>
    streamSocket.current?.readyState === WebSocket.OPEN;

  datafeed.current.currentGlobalStocksState = currentGlobalStocksState;

  const [currentSymHiroOverview, setCurrentSymHiroOverview] = useRecoilState(
    currentSymHiroOverviewState,
  );
  datafeed.current.broadCastPriceUpdate = (
    symbol: string,
    price: number,
    timestamp: number,
  ) => {
    if (containerMode == 'canvas' || currentSymHiroOverview?.sym !== symbol) {
      return;
    }
    setCurrentSymHiroOverview({
      ...(currentSymHiroOverview ?? {}),
      sym: symbol,
      price,
      priceTimestamp: timestamp,
    });
  };

  datafeed.current.broadCastSignalUpdate = (
    symbol: string,
    signal: number,
    timestamp: number,
  ) => {
    if (containerMode == 'canvas' || currentSymHiroOverview?.sym !== symbol) {
      return;
    }
    setCurrentSymHiroOverview({
      ...currentSymHiroOverview,
      sym: symbol,
      signal,
      signalTimestamp: timestamp,
    });
  };

  useEffect(() => {
    if (currentDatafeed != null && webWorker != null) {
      currentDatafeed.setWebWorker(webWorker);
    }
  }, [webWorker, currentDatafeed]);

  useEffect(() => {
    datafeed.current.resetData();
    for (const { symbolLineParams } of Object.values(
      datafeed.current.signalSeries,
    )) {
      currentDatafeed.disableSignalSeries(symbolLineParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sym]);

  useEffect(() => {
    currentDatafeed.enableSignalSeries({
      kind: 'price',
      symbol: sym,
    });

    const deltaSymbolParams: Omit<SymbolParams, deltaLineParams> = {
      kind: 'hiro',
      symbol: sym,
    };

    (allTradesEnabled && totalOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Total',
        optionOrigin: 'All',
        isFiltered: true,
      },
    });

    (allTradesEnabled && putOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Puts',
        optionOrigin: 'All',
        isFiltered: true,
      },
    });

    (allTradesEnabled && callOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Calls',
        optionOrigin: 'All',
        isFiltered: true,
      },
    });

    (nextExpiryEnabled && totalOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Total',
        optionOrigin: 'NextExpiry',
        isFiltered: true,
      },
    });

    (nextExpiryEnabled && putOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Puts',
        optionOrigin: 'NextExpiry',
        isFiltered: true,
      },
    });

    (nextExpiryEnabled && callOptionAccumulationEnabled
      ? currentDatafeed.boundEnableSignalSeries
      : currentDatafeed.boundDisableSignalSeries)({
      ...deltaSymbolParams,
      deltaLineParams: {
        optionAccumulationType: 'Calls',
        optionOrigin: 'NextExpiry',
        isFiltered: true,
      },
    });

    update({
      hiroSelectedSeriesList: Object.keys(currentDatafeed.signalSeries)
        .sort()
        .map((seriesName) => ({
          seriesName,
          symbolLineParams:
            currentDatafeed.signalSeries[seriesName].symbolLineParams,
        })),
    });

    setRerenderCnt(-rerenderCnt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allTradesEnabled,
    nextExpiryEnabled,
    totalOptionAccumulationEnabled,
    putOptionAccumulationEnabled,
    callOptionAccumulationEnabled,
    sym,
    currentDatafeed,
  ]);

  for (const series of Object.values(currentDatafeed.signalSeries)) {
    if (series.symbolLineParams.symbol != sym) {
      currentDatafeed.boundDisableSignalSeries(series.symbolLineParams);
    }
  }

  const beginVisibleRange = datafeed.current.beginVisibleRange.valueOf();
  const endVisibleRange = datafeed.current.endVisibleRange?.valueOf();

  const validHiroIndicators = hiroIndicators
    .filter(
      (hiroIndicator) =>
        Object.values(datafeed.current.signalSeries).some((series) =>
          genericSymbolineParamsEqual(
            hiroIndicator.symbolLineParams,
            series.symbolLineParams,
          ),
        ) && hiroIndicatorValid(hiroIndicator),
    )
    .map((hiroIndicator) => {
      const indicatorMeta = indicatorFromName.get(hiroIndicator.indicatorName)!;
      return [
        hiroIndicator,
        indicatorMeta,
        range(
          Math.max(
            hiroIndicator.constructorInputs?.length ?? 0,
            indicatorMeta?.constructorInputs.length ?? 0,
          ),
        ).map((i) => {
          const userInput = hiroIndicator.constructorInputs?.[i];
          const paramMeta = indicatorMeta.constructorInputs[i];

          let constructorVal;

          if (userInput != null && userInput.length > 0) {
            constructorVal = parseFloat(userInput);
            if (isNaN(constructorVal)) {
              constructorVal = paramMeta?.defaultValue;
            }
          } else {
            constructorVal = paramMeta?.defaultValue;
          }
          if (constructorVal != null && paramMeta?.inputTransform != null) {
            constructorVal = paramMeta.inputTransform(constructorVal);
          }
          return {
            userInput,
            paramMeta,
            constructorVal,
          };
        }),
      ];
    });

  const maxIndicatorsInterval = Math.max(
    0,
    ...validHiroIndicators.flatMap(
      ([_hiroIndicator, _indicatorMeta, constructorParams]) =>
        constructorParams
          .filter(
            ({ paramMeta, constructorVal }): boolean =>
              ((paramMeta?.paramName as string) ?? '').includes('Interval') &&
              constructorVal != null &&
              !isNaN(constructorVal),
          )
          .map(({ constructorVal }) => constructorVal),
    ),
  );

  const indicatorContextBegin =
    beginVisibleRange - maxIndicatorsInterval * candleDurationMs;

  const [colorsLastValue, setColorsLastValue] = useState(hiroSeriesColors);
  const [colorsChangeTimestamp, setColorsChangeTimestamp] = useState(0);
  useEffect(() => {
    if (!isEqual(colorsLastValue, hiroSeriesColors)) {
      setColorsLastValue(hiroSeriesColors);
      setColorsChangeTimestamp(new Date().valueOf());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    colorsLastValue,
    hiroSeriesColors?.all?.total,
    hiroSeriesColors?.all?.put,
    hiroSeriesColors?.all?.call,
    hiroSeriesColors?.nextExp?.total,
    hiroSeriesColors?.nextExp?.put,
    hiroSeriesColors?.nextExp?.call,
    hiroSeriesColors?.price?.total,
  ]);
  const colorChangeRecently =
    new Date().valueOf() - colorsChangeTimestamp < 500;

  const deltaSeriesList = Object.keys(datafeed.current.signalSeries)
    .sort()
    .filter(
      (name) =>
        datafeed.current.signalSeries[name].symbolLineParams.kind === 'hiro',
    )
    .map((name) => {
      const series = datafeed.current.signalSeries[name];
      const { optionAccumulationType, optionOrigin } =
        series.symbolLineParams.deltaLineParams;
      const color = hiroSeriesColors[
        OPTION_ORIGIN_TO_PALETTE.get(optionOrigin)!
      ][OPTION_ACCUM_TO_PALETTE.get(optionAccumulationType)!] as string;
      return new DeltaChartSeries(
        name,
        series.symbolLineParams,
        series.mutatedCandles?.filter(
          (c) =>
            c.time >= indicatorContextBegin &&
            (endVisibleRange == null || c.time <= endVisibleRange) &&
            (showExtendedHours || skipNonMarketHours.dateInMarketHours(c.date)),
        ) ?? [],
        [color],
        colorChangeRecently,
      );
    });

  const priceSeriesList = Object.keys(datafeed.current.signalSeries)
    .sort()
    .filter(
      (name) =>
        datafeed.current.signalSeries[name].symbolLineParams.kind === 'price',
    )
    .map((name) => {
      const series = datafeed.current.signalSeries[name];

      return new PriceChartSeries(
        name,
        series.symbolLineParams,
        series.mutatedCandles?.filter(
          (c) =>
            c.time >= indicatorContextBegin &&
            (endVisibleRange == null || c.time <= endVisibleRange) &&
            (showExtendedHours || skipNonMarketHours.dateInMarketHours(c.date)),
        ) ?? [],
        [hiroSeriesColors.price.total],
        [theme.colorMode === ColorMode.LIGHT ? 'white' : 'black'],
        priceLineType,
        colorChangeRecently,
      );
    });

  const indicatorList = validHiroIndicators
    .map(([hiroIndicator, indicatorMeta, constructorParams]) => {
      if (indicatorMeta == null) {
        return null;
      }
      const series = Object.values(datafeed.current.signalSeries).find(
        (series) =>
          genericSymbolineParamsEqual(
            hiroIndicator.symbolLineParams,
            series.symbolLineParams,
          ),
      );

      if (series == null) {
        return;
      }
      const yScale =
        indicatorMeta.panePlacement != 'Main'
          ? 'own'
          : series.symbolLineParams.kind === 'price'
            ? 'price'
            : 'delta';

      const inputChartSeries = (
        yScale === 'own'
          ? [...priceSeriesList, ...deltaSeriesList]
          : yScale === 'price'
            ? priceSeriesList
            : deltaSeriesList
      ).find((series) =>
        genericSymbolineParamsEqual(
          series.symbolLineParams,
          hiroIndicator.symbolLineParams,
        ),
      );

      if (inputChartSeries == null) {
        return null;
      }

      let indicatorCls = indicatorMeta.cls;
      const isWrapperedBollingBands =
        hiroIndicator.indicatorName == 'Bollinger Bands Width (BBW)';
      if (isWrapperedBollingBands) {
        indicatorCls = trading_signals.BollingerBands;
      }

      let indicatorKernel = new indicatorCls(
        ...constructorParams.map(({ constructorVal }) => constructorVal),
      );
      if (isWrapperedBollingBands) {
        indicatorKernel = new indicatorMeta.cls(indicatorKernel);
      }

      let exceptionSeen = false;
      const data = inputChartSeries.candles
        .map((candle) => {
          if (exceptionSeen) {
            return;
          }
          let value;
          try {
            switch (indicatorMeta.inputType) {
              case 'number':
                value = indicatorKernel.add(candle.close);
                break;
              case 'HighLowClose':
              case 'HighLow':
              case 'OpenHighLowCloseVolume':
              case 'HighLowCloseVolume':
                value = indicatorKernel.add(candle);
                break;
              default:
                throw Exception('Not implemented');
            }
          } catch (_e: Exception) {
            exceptionSeen = true;
          }

          if (value == null || (typeof value == 'number' && isNaN(value))) {
            return null;
          } else {
            if (indicatorMeta.resultType == 'number') {
              return {
                date: candle.date,
                time: candle.time,
                value,
              };
            } else {
              return {
                date: candle.date,
                time: candle.time,
                ...value,
              };
            }
          }
        })
        .filter(Boolean);

      let chartSeriesClass;
      switch (indicatorMeta.resultType) {
        case 'number':
          chartSeriesClass = IndicatorSingleLineChartSeries;
          break;
        case 'BandsResult':
          chartSeriesClass = BollingerBandsChartSeries;
          break;

        case 'MACDResult':
          chartSeriesClass = MACDChartSeries;
          break;
        default:
          throw Error(
            `Unsupported chart series type ${indicatorMeta.indicatorName}`,
          );
      }

      return new chartSeriesClass(
        `${hiroIndicator.seriesName} - ${hiroIndicator.indicatorName}`,
        yScale,
        indicatorMeta,
        data,
        hiroIndicator.colors,
      );
    })
    .filter(Boolean);

  if (maxIndicatorsInterval > 0) {
    for (const series of [...deltaSeriesList, ...priceSeriesList]) {
      series.candles = series.candles.filter(
        (c) => c.time >= beginVisibleRange,
      );
    }
  }

  const beginVisibleRangeBegDay = datafeed.current.beginVisibleRange
    .tz(ET)
    .startOf('day');
  const endVisibleRangeEndDay = (datafeed.current.endVisibleRange ?? dayjs())
    .add(1, 'day')
    .tz(ET)
    .startOf('day');

  const { data: hiroKeyLevelList } = useHiroKeyLevels(
    {
      sym,
      beginDate: beginVisibleRangeBegDay,
      endDate: endVisibleRangeEndDay,
    },
    {
      enabled: showKeyLevels || containerMode == 'standalone',
    },
  );

  const hiroKeyLevels = new KeyLevelChartSeries(
    'hiroKeyLevels',
    showKeyLevels ? (hiroKeyLevelList ?? []) : [],
  );

  const beginVisibleRangeBegDayTs = beginVisibleRangeBegDay.valueOf();
  const endVisibleRangeEndDayTs = endVisibleRangeEndDay.valueOf();
  const alerts = useRef<AlertData[]>([]);

  const fetchAlerts = () =>
    fetchAPIWithLog<AlertData[]>(
      `v1/alerts?syms=${encodeURIComponent(sym)}&start=${beginVisibleRangeBegDay.format('YYYY-MM-DD')}&end=${endVisibleRangeEndDay.format('YYYY-MM-DD')}`,
    )
      .then(filterHiroAlerts)
      .then((alertsAry: AlertData[]) => {
        alerts.current = alertsAry.map((alert) => ({
          alert_time: new Date(alert.alert_time),
          category: HiroAlertCategoryFromAlertCategory[
            alert.category
          ] as string,
        }));
      });
  useEffect(
    () => {
      if (webWorker == null) {
        return;
      }
      void fetchAlerts();
      if (endVisibleRangeEndDay > dayjs()) {
        return unsupressedSetInterval(
          webWorker,
          () => void fetchAlerts(),
          20_000,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [webWorker, beginVisibleRangeBegDayTs, endVisibleRangeEndDayTs, sym],
  );

  const alertSeries = new AlertsChartSeries(
    showAlerts
      ? (alerts.current?.filter(
          (a) =>
            datafeed.current.beginVisibleRange?.isSameOrBefore(a.alert_time) &&
            (datafeed.current.endVisibleRange == null ||
              datafeed.current.endVisibleRange.isSameOrAfter(a.alert_time)),
        ) ?? [])
      : [],
  );

  useEffect(
    () => {
      if (webWorker == null) {
        return;
      }
      return unsupressedSetInterval(
        webWorker,
        datafeed.current.debouncedRerenderChart,
        5_000,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [webWorker, datafeed.current],
  );

  // clean up polling artifacts on unmount of the component
  useEffect(
    () => () => {
      if (datafeed.current == null) {
        return;
      }
      for (const series of Object.values(datafeed.current.signalSeries)) {
        datafeed.current.boundDisableSignalSeries(series.symbolLineParams);
      }
    },
    [],
  );
  const chartId = useMemo(() => `hiroV3Chart${uuidv4().replace(/-/g, '')}`, []);

  return (
    <HiroD3Chart
      chartId={chartId}
      sym={sym}
      deltaSeriesList={deltaSeriesList}
      priceSeriesList={priceSeriesList}
      mainPaneIndicatorList={indicatorList.filter(
        (i) => i.indicatorMeta.panePlacement == 'Main',
      )}
      bottomIndicatorList={indicatorList.filter(
        (i) => i.indicatorMeta.panePlacement == 'Bottom',
      )}
      hiroKeyLevels={hiroKeyLevels}
      alertSeries={alertSeries}
      rerenderCnt={rerenderCnt}
      datafeedRef={datafeed}
      hiroLineType={hiroLineType}
      resetZoomRef={resetZoomRef}
      showPrice={showPrice}
      autoRangeDeltaAxis={autoRangeDeltaAxis}
      setAutoRangeDeltaAxis={setAutoRangeDeltaAxis}
      autoRangePriceAxis={autoRangePriceAxis}
      setAutoRangePriceAxis={setAutoRangePriceAxis}
    />
  );
};

const drawYAxisClosingVals = (
  yAxisSvg,
  chartSeriesList: ChartSeries[],
  scale,
  formatter: (n: number) => string,
) => {
  const labels = yAxisSvg
    .selectAll('g.hiro-closing-y-axis-label')
    .data(chartSeriesList);

  labels.exit().remove();

  const gEnter = labels
    .enter()
    .append('g')
    .attr('class', 'hiro-closing-y-axis-label')
    .attr('z-index', 100);
  gEnter
    .append('rect')
    .attr('width', '100%')
    .attr('height', '13')
    .attr('transform', 'translate(0, -4.5)');
  gEnter
    .append('text')
    .attr('dy', '0.55em')
    .attr('transform', 'translate(0,0)')
    .attr('font-size', '11');

  labels
    .merge(gEnter)
    .attr('transform', (d) => {
      const closingValue = d.candles[d.candles.length - 1]?.close ?? 0;
      const yTranslate = scale(closingValue);
      if (isNaN(yTranslate)) {
        return undefined;
      } else {
        return `translate(0, ${yTranslate})`;
      }
    })
    .attr('fill', (d) => d.colors[0])
    .style('opacity', (d) =>
      typeof d.candles[d.candles.length - 1]?.close == 'undefined' ? '0' : '1',
    )
    .select('text')
    .attr('fill', (series) => series.latestValueLabelColor ?? 'white')
    .text((d) =>
      formatter(d.candles[d.candles.length - 1]?.close ?? 0, {
        maximumSignificantDigits: 3,
      }),
    );
};

const currencyLabel = (n) =>
  Intl.NumberFormat('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const deltaLabel = (n) =>
  formatAsCompactNumber(n, { maximumSignificantDigits: 3 });

const formatMillisecond = '.%L';
const formatSecond = ':%S';
const formatMinute = '%_I:%M';
const formatHour = '%_I%p';

function getTimeFormatter(
  userTzDate: UserTzDate,
  date: Date,
  visibleDays: null | number,
  begDay: Date,
  begMonth: Date,
  begYear: Date,
  showExtendedHours: boolean,
) {
  let subDayFmt = '';
  if (showExtendedHours || !skipNonMarketHours.isMarketOpen(date)) {
    if (timeSecond(userTzDate) < userTzDate) {
      subDayFmt = formatMillisecond;
    } else if (timeMinute(userTzDate) < userTzDate) {
      subDayFmt = formatSecond;
    } else if (timeHour(userTzDate) < userTzDate) {
      subDayFmt = formatMinute;
    } else if (timeDay(userTzDate) < userTzDate) {
      subDayFmt = formatHour;
    }
  }
  if (visibleDays < 3 && subDayFmt != '') {
    return subDayFmt;
  }

  let dayPlusFmt = '';
  if (userTzDate < begYear) {
    dayPlusFmt = '%Y-%_m-%_d';
  } else if (userTzDate < begMonth) {
    dayPlusFmt = '%_m-%_d';
  } else {
    dayPlusFmt = '%_d';
  }
  return `${dayPlusFmt} ${subDayFmt}`;
}

function timeTickFormat(
  userTzDate: UserTzDate,
  date: Date,
  visibleDays: number,
  begDay: Date,
  begMonth: Date,
  begYear: Date,
  showExtendedHours: boolean,
) {
  return timeFormat(
    getTimeFormatter(
      userTzDate,
      date,
      visibleDays,
      begDay,
      begMonth,
      begYear,
      showExtendedHours,
    ),
  )(userTzDate).toLowerCase();
}

const genTimeTickFormat = (
  timeZone: string,
  visibleDays: null | number,
  showExtendedHours: boolean,
) => {
  const begDayjs = dayjs().tz(timeZone).startOf('day');
  const transformIntoUserTimezone = (d: Dayjs): EtDate =>
    new Date(
      d.toDate().toLocaleString('en-US', {
        timeZone,
      }),
    );

  const begDay = transformIntoUserTimezone(begDayjs);
  const begMonth = transformIntoUserTimezone(begDayjs.startOf('month'));
  const begYear = transformIntoUserTimezone(begDayjs.startOf('year'));

  return (date: Date) =>
    timeTickFormat(
      new Date(
        date.toLocaleString('en-US', {
          timeZone,
        }),
      ),
      date,
      visibleDays,
      begDay,
      begMonth,
      begYear,
      showExtendedHours,
    );
};

// a formatting of the indicator output for display
// multiple results means multiple lins
const indicatorDisplayStrings = (
  resultType: IndicatorResultType,
  yScale: 'price' | 'delta' | 'own',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datum: any,
): string[] => {
  const fmt = yScale == 'hiro' ? deltaLabel : currencyLabel;
  switch (resultType) {
    case 'number':
      return [fmt(datum.value)];
    case 'BandsResult':
      return [
        `Lower: ${fmt(datum.lower)}`,
        `Middle: ${fmt(datum.middle)}`,
        `Upper: ${fmt(datum.upper)}`,
      ];
    case 'DMAResult':
      return [`Long: ${fmt(datum.long)}`, `Short: ${fmt(datum.short)}`];
    case 'MACDResult':
      return [
        `Histogram: ${fmt(datum.histogram)}`,
        `MACD: ${fmt(datum.macd)}`,
        `Signal: ${fmt(datum.signal)}`,
      ];
    case 'LinearRegressionResult':
      return [
        `Prediction: ${fmt(datum.prediction)}`,
        `Slope: ${fmt(datum.slope)}`,
        `Intercept: ${fmt(datum.intercept)}`,
      ];
    case 'StochasticResult':
      return [`D: ${fmt(datum.stochD)}`, `K: ${fmt(datum.stochK)}`];
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw Exception(`Unsupported resultType=${resultType}`);
  }
};

function makeYAxisStretchDrag({
  autoRangeRef,
  disableAutoRange,
  scaleRef,
  zoomTransformRef,
  stateKey,
  renderRef,
  afterDrag,
}) {
  return d3
    .drag()
    .on('start', function (event) {
      if (autoRangeRef.current != null) {
        disableAutoRange();
      }
      const scale = scaleRef.current;
      const range = scale.range();
      const height = Math.abs(range[0] - range[1]) || 1;
      const startY = event.y;
      const anchor = scale.invert(startY);
      d3.select(this).property(stateKey, {
        domain: scale.domain().slice(),
        anchor,
        startY,
        height,
      });
    })
    .on('drag', function (event) {
      const s = d3.select(this).property(stateKey);
      if (s == null) {
        return;
      }
      const factor = Math.exp(((event.y - s.startY) / s.height) * 1.5);
      const newDomain = [
        s.anchor - (s.anchor - s.domain[0]) * factor,
        s.anchor + (s.domain[1] - s.anchor) * factor,
      ];
      scaleRef.current.domain(newDomain);
      zoomTransformRef.current = d3.zoomIdentity;
      renderRef.current?.();
      afterDrag?.();
    });
}

function makeCrosshair({
  chartXScaleRef,
  chartYScaleRef,
  timeZone,
  displayTime,
}: {
  chartXScaleRef: Ref;
  chartYScaleRef: Ref;
  timeZone: string;
  displayTime: boolean;
}) {
  return fc
    .annotationSvgCrosshair()
    .xScale(chartXScaleRef.current)
    .yScale(chartYScaleRef.current)
    .yLabel((_d) => '')
    .xLabel((d) =>
      !displayTime
        ? ''
        : dayjs(
            new Date(
              dayjs(chartXScaleRef.current.invert(d.x))
                .toDate()
                .toLocaleString('en-US', {
                  timeZone,
                }),
            ),
          ).format('MMM D h:mm:ss'),
    )
    .decorate((selection) => {
      selection
        .select('g.vertical text')
        .attr('font-size', '11')
        .attr('fill', 'currentColor');
      selection.selectAll('.point>path').style('display', 'none');
    });
}
